import { cva, type VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';

import { cn } from '../lib/cn';

const avatarVariants = cva(
  'flex shrink-0 items-center justify-center rounded-full font-medium uppercase',
  {
    variants: {
      tone: {
        brand: 'bg-brand-800 text-white/70',
        light: 'bg-brand-50 text-brand-600',
        /**
         * Une couleur par personne, tirée de son nom.
         *
         * Les classes réelles sont posées par `couleurDepuisNom` : une variante
         * `cva` ne peut pas dépendre d'une prop, et Tailwind n'accepte pas les
         * noms de classe construits à l'exécution. Celle-ci n'existe donc que
         * pour nommer l'intention dans l'API.
         */
        auto: '',
        /** Inconnu ou anonyme : sombre, sans couleur propre. */
        neutral: 'bg-content text-white',
      },
      size: {
        sm: 'size-8 text-[0.625rem]',
        md: 'size-10 text-xs',
        lg: 'size-12 text-sm',
      },
    },
    defaultVariants: { tone: 'brand', size: 'md' },
  },
);

/**
 * Les huit pastilles, en dur et non calculées.
 *
 * Tailwind lit les classes dans le source : une classe assemblée à
 * l'exécution (`bg-avatar-${n}`) ne serait jamais générée. Les teintes, elles,
 * vivent dans `theme.css` — c'est là qu'on lit pourquoi ce sont celles-là.
 */
const COULEURS_AVATAR = [
  'bg-avatar-1 text-white',
  'bg-avatar-2 text-white',
  'bg-avatar-3 text-white',
  'bg-avatar-4 text-white',
  'bg-avatar-5 text-white',
  'bg-avatar-6 text-white',
  'bg-avatar-7 text-white',
  'bg-avatar-8 text-white',
] as const;

/**
 * La couleur d'une personne, dérivée de son nom.
 *
 * Déterministe et sans état : « Camille Dupont » tombe toujours sur la même
 * pastille, ici, sur l'autre écran, et après rechargement. Une couleur tirée
 * au sort à l'affichage aurait fait clignoter la liste à chaque rendu, et
 * aurait ôté à la couleur le peu qu'elle apporte — un repère qu'on reconnaît
 * avant d'avoir lu le nom.
 *
 * Hachage FNV-1a, pris pour sa dispersion sur des chaînes courtes : une somme
 * de codes de caractères regroupe les anagrammes et les noms de même longueur
 * sur la même teinte. Les nombres sont bornés par `>>> 0` — au-delà de 2^31,
 * les opérateurs binaires de JavaScript repassent en signé.
 */
export function couleurDepuisNom(nom: string): string {
  let hachage = 2166136261;

  for (let index = 0; index < nom.length; index += 1) {
    hachage ^= nom.charCodeAt(index);
    hachage = Math.imul(hachage, 16777619) >>> 0;
  }

  // FNV-1a seul laisse les bits de poids faible ne dépendre que de ceux des
  // caractères : deux noms dont les lettres diffèrent d'un multiple de 8
  // tombaient sur la même teinte. Un dernier brassage répartit l'information
  // sur tous les bits avant le modulo.
  hachage ^= hachage >>> 16;
  hachage = Math.imul(hachage, 2246822507) >>> 0;
  hachage ^= hachage >>> 13;

  return COULEURS_AVATAR[(hachage >>> 0) % COULEURS_AVATAR.length] ?? COULEURS_AVATAR[0];
}

export type AvatarProps = VariantProps<typeof avatarVariants> & {
  /** Nom complet : les initiales et, en `tone="auto"`, la couleur en sont dérivées. */
  name: string;
  /** Remplace les initiales (ex. une silhouette pour un anonyme). */
  icon?: ReactNode;
  className?: string;
};

/** Initiales de l'utilisateur dans une pastille ronde. */
export function Avatar({ name, tone, size, icon, className }: AvatarProps) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('');

  return (
    <span
      aria-hidden="true"
      className={cn(
        avatarVariants({ tone, size }),
        tone === 'auto' && couleurDepuisNom(name),
        className,
      )}
    >
      {icon ?? initials}
    </span>
  );
}

export { avatarVariants };
